import ApiModel               from '@widesk-core/models/ApiModel';
import { makeObservable }     from 'mobx';
import { observable }         from 'mobx';
import { action }             from 'mobx';
import ApiCollection          from '@widesk-core/models/ApiCollection';
import _uniqBy                from 'lodash/uniqBy';
import StonecodeFileFileModel from '@models/file/StonecodeFileFileModel';
import cacheSystem            from '@widesk-core/models/apiCacheSystem';

/** Préfixe des urns de fichiers, tel que le cache les indexe : `$:file:stonecode_file_file:12`. */
const FILE_URN_PREFIX = '$:file:stonecode_file_file:';

type Item = {
	source: ApiModel;
	loading: boolean;
	loaded: boolean;
	files: StonecodeFileFileModel[];
	/**
	 * Incrémenté à chaque invalidation (`force`). Une requête partie avant celle-ci porte une
	 * génération périmée : son résultat est jeté à l'arrivée plutôt qu'écrit par-dessus.
	 */
	generation: number;
}

type FetchOptions = {
	force?: boolean;
	fetchCollection?: boolean; // Si une collection est liée au model source, les Files seront récupérés pour toute la collection
}

class FileStore {
	public itemDictionary: Record<Urn, Item> = {};

	constructor() {
		makeObservable(this, {
			itemDictionary: observable,
			setItemDictionary: action,
		});
	}

	public setItemDictionary(key: Urn, item: Item) {
		this.itemDictionary[key] = item;
	}

	/**
	 * Charge les fichiers des sources demandées.
	 *
	 * Deux règles, toutes deux nées du même bug : après la suppression d'une image, celle-ci revenait
	 * à l'écran.
	 *
	 *  1. **Une requête forcée part toujours**, même si un chargement est déjà en cours pour cette
	 *     source. Sans ça, `force` était sans effet dès qu'une requête traînait — et il en traîne
	 *     souvent, `getFilesForModel` en programmant une à CHAQUE lecture.
	 *  2. **La dernière requête partie est la seule à pouvoir écrire.** Chaque chargement incrémente
	 *     la génération de ses sources et ne s'écrit que si elle n'a pas bougé entre-temps. Une
	 *     réponse partie avant la suppression, revenue après, décrit un état révolu : on la jette.
	 *
	 * La réponse REMPLACE la liste au lieu de s'y ajouter : elle porte tous les fichiers de la source
	 * (le filtre porte sur `typeSource.source.entityUrn`), elle fait donc autorité. Fusionner
	 * ramenait les fichiers détruits depuis.
	 */
	public async fetchForModel(model: ApiModel, options?: FetchOptions) {
		const sources = options?.fetchCollection ?
			(model.collection as ApiCollection<ApiModel> | undefined)?.map(m => m) || [model] :
			[model];

		// On ajoute toutes les sources dans le dictionnaire (si elle n'existe pas déjà)
		sources.forEach(source => {
			if (!this.itemDictionary[source.urn]) {
				this.setItemDictionary(source.urn, { source, loading: false, loaded: false, files: [], generation: 0 });
			}
		});

		const forced = new Set(options?.force ? sources.map(source => source.urn) : []);

		// Le connecteur sert ses listes depuis son cache AVANT de regarder la moindre option : sans cette
		// invalidation, un rechargement forcé ne partait même pas sur le réseau et rendait l'état
		// d'avant. C'est ce qui faisait réapparaître une image tout juste supprimée.
		//
		// Toute la famille des fichiers, et pas seulement la source demandée : le cache indexe ses
		// requêtes par les urns qu'elles ont RENDUS — des fichiers — jamais par celui de la source qui
		// sert à les filtrer.
		if (forced.size) cacheSystem.removeUrnStartWith(FILE_URN_PREFIX);

		forced.forEach(urn => {
			const item = this.itemDictionary[urn]!;

			this.setItemDictionary(urn, { ...item, loading: false, loaded: false, files: [] });
		});

		const itemsToFetch = Object.values(this.itemDictionary)
			.filter(item => forced.has(item.source.urn) || (!item.loading && !item.loaded));

		if (!itemsToFetch.length) return;

		// Génération retenue au départ : c'est elle qui départage cette requête des suivantes.
		const generations = new Map<Urn, number>();

		itemsToFetch.forEach(item => {
			const generation = item.generation + 1;

			generations.set(item.source.urn, generation);
			this.setItemDictionary(item.source.urn, { ...item, loading: true, generation });
		});

		const fileCollection = new ApiCollection(StonecodeFileFileModel);

		// `available` : la suppression d'un fichier est logique, pas physique — sans ce filtre, l'API
		// continue de rendre ceux qu'on vient de supprimer, et l'image réapparaissait au premier
		// rechargement suivant le retrait. C'est le filtre que pose déjà tout le reste du code (voir
		// `updateSingleFileForModelUrn`, `ProjectImage`) ; ce store était le seul à s'en passer.
		await fileCollection
			.setRequiredFilter('fileStatus.reference', 'available')
			.listBy(itemsToFetch.map(item => item.source.urn), 'typeSource.source.entityUrn');
		await fileCollection.whenIsLoaded(f => [
			f.typeSource.source,
			f.typeSource.type,
			f.mimeType,
		]);

		itemsToFetch.forEach(item => {
			const current = this.itemDictionary[item.source.urn];

			// Une requête plus récente est passée depuis : c'est elle qui fait foi, pas celle-ci.
			if (!current || current.generation !== generations.get(item.source.urn)) return;

			this.setItemDictionary(item.source.urn, {
				...current,
				loading: false,
				loaded: true,
				files: _uniqBy(
					fileCollection.filter(f => f.typeSource.source.entityUrn === item.source.urn),
					f => f.id,
				),
			});
		});
	}

	/**
	 * Retire des fichiers d'une source sans rien redemander au serveur.
	 *
	 * Appelé après une suppression réussie : on SAIT ce qu'on vient de détruire. Le redemander nous
	 * exposait au cache de requêtes du connecteur, qui resservait la liste d'avant — l'image supprimée
	 * réapparaissait aussitôt, et aucune requête ne partait même sur le réseau.
	 */
	public forget(model: ApiModel, matches: (file: StonecodeFileFileModel) => boolean) {
		const item = this.itemDictionary[model.urn];

		if (!item) return;

		this.setItemDictionary(model.urn, { ...item, files: item.files.filter(file => !matches(file)) });
	}

	/**
	 * Ajoute un fichier à la liste d'une source sans rien demander au serveur — pendant que ses
	 * propres listes n'en savent encore rien.
	 *
	 * Même raison que `forget`, en sens inverse : le service de fichiers met un instant à indexer un
	 * fichier tout juste créé. Recharger après un envoi rapportait la liste d'AVANT — donc l'image
	 * précédente, réapparue à la place de celle qu'on venait de déposer.
	 */
	public remember(model: ApiModel, file: StonecodeFileFileModel) {
		const item = this.itemDictionary[model.urn];

		if (!item) return;

		this.setItemDictionary(model.urn, {
			...item,
			files: [...item.files.filter(f => f.id !== file.id), file],
		});
	}

	public getFilesForModel(model: ApiModel, options?: FetchOptions) {
		(() => setTimeout(() => this.fetchForModel(model, options)))();

		return this.itemDictionary[model.urn]?.files || [];
	}
}

export default new FileStore();